// @cogo/runtime-v2 — Strict runtime with stateContract enforcement

// AppRuntimeCore — React-free app orchestration (shared by web + Flutter)
export { createAppRuntimeCore, matchRouteCore } from "./app-runtime-core.js"
export type {
  AppRuntimeCore,
  AppRuntimeCoreOptions,
  AppRuntimeCoreRef,
  EventExecutionReceipt,
  NavigateOptions,
  NavigationReceipt,
  SandboxAwarePlatform,
} from "./app-runtime-core.js"
export {
  buildTickFlowExecutionReceipt,
  verifyTickFlowExecutionReceipt,
} from "./tick-flow-execution-proof.js"
export type { TickFlowExecutionReceipt } from "./tick-flow-execution-proof.js"
export { ACTION_FLOW_ENGINE_SOURCE_HASH } from "./source-authority.js"

// State Management
export { StateManager } from "./state-manager"
export type { StateManagerOptions, GovernanceMode } from "./state-manager"
export { SymbolStore, evaluateTernary, evaluateCondition, resolveLiteral } from "./symbol-store"
export { LocalStateManager } from "./local-state-manager"
export type { ReactionStep, Reaction } from "./local-state-manager"

// Expression Engine
export { ComputeEvaluator, isTruthy, toNum, splitLogicalParts, matchingParen } from "./compute-evaluator"
export type { StateAccessor } from "./compute-evaluator"
export { ExpressionEvaluator } from "./expression-evaluator"
export { ExpressionCache } from "./expression-cache"
export type { CachedExpression, CacheStats } from "./expression-cache"
export { FunctionRegistry } from "./expression-function"
export type { ExpressionFunction, FunctionParam } from "./expression-function"
export { ExpressionRegistry } from "./expression-registry"
export type { RegistryOptions } from "./expression-registry"

// Event System
export { EventDispatcher } from "./event-dispatcher"

// Data
export { DataSourceManager, materializeV4DatasetsForRuntime } from "./datasource-manager"

// Validation
export { validateNamespaces, resolveBindings } from "./namespace-validator"

// Navigation History
export { NavigationHistoryStack } from "./navigation-history"
export type { NavigationEntry } from "./navigation-history"

// Platform Actions (D-471-03 bucket b — ported from v1)
export {
  evaluateFormula,
  extractFormulaDependencies,
  registerFormulaActions,
} from "./formula-actions"
export type {
  FormulaActionEngine,
  FormulaCellRef,
  FormulaDependencyResult,
  FormulaEvalParams,
  FormulaEvaluationResult,
  FormulaPosition,
  FormulaRangeRef,
} from "./formula-actions"
export { registerWalletActions } from "./wallet-actions"
export type { WalletConfig } from "./wallet-actions"
export { registerSupabaseActions } from "./supabase-actions"
export type { SupabaseLikeClient, SupabaseActionEngine } from "./supabase-actions"
export { registerCfActions } from "./cf-actions"
export type { CfActionEngine } from "./cf-actions"
export { registerGoogleActions } from "./google-actions"
export type { GoogleActionEngine } from "./google-actions"
export { SubscriptionRegistry } from "./subscription-registry"
export type { RealtimeChannelLike } from "./subscription-registry"

// Debug Inspector (D-471-03 bucket c)
export { CogoInspector } from "./inspector"
export type { FlowTrace, StepTrace, ExpressionTrace } from "./inspector"

// Integrity (D-471-03 bucket c — cogo-headless cross-milestone test)
export { IntegrityVerifier } from "./integrity-verifier"
export type { BlockManifestSig } from "./integrity-verifier"

// Renderer-neutral RenderPack transport
export {
  RENDER_PACK_SCHEMA_VERSION,
  compileCogoRenderPack,
  decodeRenderPack,
  encodeRenderPack,
  materializeRenderPackTree,
} from "./render-pack"
export type {
  CogoRenderPackInput,
  CompileRenderPackOptions,
  MaterializedRenderPackTree,
  RenderPack,
  RenderPackBinding,
  RenderPackDesignOp,
  RenderPackDiagnostic,
  RenderPackLayoutOp,
  RenderPackNode,
  RenderPackPropOp,
} from "./render-pack"

// ActionFlow Engine
export { ActionConsumerCatalog } from "./action-consumer-catalog"
export {
  createActionConsumerObservationScope,
  createActionConsumerObservationScopeFactory,
  hashActionConsumerValueInScope,
  observeActionConsumerOutcome,
  resolveActionConsumerStateTarget,
} from "./action-consumer-contract"
export {
  buildActionConsumerCatalogProjection,
  collectRuntimeActionConsumerContracts,
  renderActionConsumerCatalogJson,
  renderActionConsumerContractsTtl,
} from "./action-consumer-projection"
export type {
  ActionConsumerChannelId,
  HostChannelId,
  ParameterChannelId,
  ProjectedActionConsumer,
  ProjectedConsumerRead,
  ProjectedHostCapability,
  ProjectedResultChannel,
  ResultChannelId,
  RuntimeActionConsumerCatalogProjection,
} from "@cogo/schema/action-consumer-catalog"
export type {
  ActionConsumerContract,
  ActionConsumerObservationScope,
  ActionConsumerObservationScopeFactory,
  ActionConsumerObserver,
  ActionConsumerOutcomeDescriptor,
  ActionConsumerOutcomeObservation,
  ActionConsumerOutcomeObserver,
  ActionConsumerProducerObservation,
  ActionConsumerProducerObserver,
  ActionConsumerReadObservation,
  ConsumerValueSchema,
  ContentHash,
  HostCapabilityId,
  RuntimeActionHandler,
} from "./action-consumer-contract"
export {
  ACTION_FLOW_BLOCK_NAMESPACE_CONTEXT_KEY,
  ACTION_FLOW_TRACE_CONTRACT_VERSION,
  ActionFlowEngine,
  ActionFlowExecutionError,
} from "./action-flow-engine"
export type {
  ActionFlowExecutionErrorCode,
  ActionFlowExecutionOptions,
  ActionFlowExecutionStatus,
  ActionFlowExecutionTrace,
  ActionFlowStateChange,
  ActionFlowStepTrace,
  ToastHandler,
  PlatformAdapter,
  NativeSignatureAgentResult,
} from "./action-flow-engine"
export { StoreActionRegistry } from "./store-action-registry"
export type { StoreActionHandler } from "./store-action-registry"

// Platform Adapter (v2 NoopAdapter for SSR/testing)
export { NoopAdapter } from "./platform-adapter"
// Platform Adapter base param types (v1-superset — renderer/RN web/native adapters)
export type { ToastParams, PopupParams, ShareData, GeoPosition, GeoOptions, MediaConstraints } from "./platform-adapter-base"

// Block Lifecycle
export { BlockLifecycleManager } from "./block-lifecycle"
export type { BlockInitTracker, LifecycleCallbacks } from "./block-lifecycle"

// Block Loader
export { loadBlock, isV2Block, V2_FILES } from "./block-loader"
export type { FileReader } from "./block-loader"

// Navigation Auto-Sync
export { resolveNavFromRoute } from "./nav-sync"
export type { NavSyncResult } from "./nav-sync"

// Security
export { StateContractEnforcer } from "./security/access-control"
export { AuditLog } from "./security/audit-log"
export type { AuditEntry } from "./security/audit-log"

// Governance config (Step 4 Phase 4.3 — per-runtime canary wiring)
export {
  resolveGovernanceConfig,
  readEnvGovernanceMode,
  defaultGovernanceSink,
} from "./governance-config"
export type { GovernanceConfig } from "./governance-config"
